import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./auth-layout.css";

interface AuthLayoutProps {
    children: React.ReactNode;
}

/**
 * Shared layout for authentication pages (login, register, forgot-password)
 * Left side: Brand panel with geometric accents
 * Right side: Form content (passed as children)
 */
export function AuthLayout({ children }: AuthLayoutProps) {
    const { t } = useTranslation("chrome");
    // Prevent focus on the container when clicking empty space
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            e.preventDefault();
        }
    }, []);

    const features = [
        t("authLayout.feature1"),
        t("authLayout.feature2"),
        t("authLayout.feature3"),
    ];

    return (
        <div className="h-screen flex bg-white overflow-hidden">
            {/* Left brand panel */}
            <div
                className="auth-layout-left hidden lg:flex flex-col w-[480px] xl:w-[540px] relative overflow-hidden"
                style={{ backgroundColor: '#3d4f7c' }}
                onMouseDown={handleMouseDown}
            >
                {/* Decorative geometric shapes */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {/* Large circle - top right */}
                    <div
                        className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-[0.07]"
                        style={{ backgroundColor: '#d4956a' }}
                    />
                    {/* Small circle - bottom left */}
                    <div
                        className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full opacity-[0.08]"
                        style={{ backgroundColor: '#d4956a' }}
                    />
                    {/* Diagonal line accent */}
                    <div
                        className="absolute top-1/3 -left-10 w-[300px] h-[1px] rotate-[35deg] opacity-[0.15]"
                        style={{ backgroundColor: '#d4956a' }}
                    />
                    <div
                        className="absolute top-1/3 -left-10 w-[280px] h-[1px] rotate-[35deg] translate-y-3 opacity-[0.10]"
                        style={{ backgroundColor: '#d4956a' }}
                    />
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col h-full p-10 xl:p-12">
                    {/* Logo */}
                    <div className="mb-auto">
                        <img src="/nova-logo-light.png" alt={t("authLayout.logoAlt")} className="h-14" />
                    </div>

                    {/* Center tagline */}
                    <div className="my-auto space-y-6">
                        <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight tracking-tight">
                            {t("authLayout.taglineMain")}
                            <br />
                            <span style={{ color: '#d4956a' }}>{t("authLayout.taglineHighlight")}</span>
                        </h2>
                        <p className="text-white/60 text-base leading-relaxed max-w-sm">
                            {t("authLayout.taglineDescription")}
                        </p>
                        {/* Feature highlights */}
                        <div className="flex flex-col gap-3 pt-2">
                            {features.map((item) => (
                                <div key={item} className="flex items-center gap-3">
                                    <div
                                        className="w-1.5 h-1.5 rounded-full shrink-0"
                                        style={{ backgroundColor: '#d4956a' }}
                                    />
                                    <span className="text-white/70 text-sm">{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-auto pt-8">
                        <p className="text-white/30 text-xs">
                            {t("authLayout.copyright", { year: new Date().getFullYear() })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Right form section */}
            {/*
              不能用 justify-center 居中：内容高于视口（浏览器放大 150%+ / 矮窗口）时，
              flex 居中会把溢出平均分到上下两侧，上侧溢出落在滚动起点之前、永远滚不到——
              登录页顶部的标题和 Clerk/Legacy 切换 tab 会被裁掉。改用内层 my-auto：
              有富余时等效居中，不够时从顶部排起、可正常滚动。
            */}
            <div
                className="auth-layout-right flex-1 flex flex-col items-center px-6 sm:px-12 lg:px-16 py-4 bg-white relative z-10 h-full overflow-y-auto"
                tabIndex={-1}
                onMouseDown={handleMouseDown}
            >
                <div className="my-auto w-full flex flex-col items-center" onMouseDown={handleMouseDown}>
                    {/* Mobile logo - only visible on small screens */}
                    <div className="lg:hidden mb-6">
                        <img src="/nova-logo-dark.png" alt={t("authLayout.logoAlt")} className="h-10" />
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
}
